import { FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import companiesApi from "@/api/companiesApi";
import ApiError from "@/model/ApiError";
import UserIdsSelect from "@/components/UserIdsSelect/UserIdsSelect";
import "@/pages/CreateCompanyPage/CreateCompanyPage.scss";

function trimOrUndefined(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function CreateCompanyPage() {
  const { t } = useTranslation();
  const { session } = useAuth();
  const navigate = useNavigate();

  const [userIds, setUserIds] = useState<string[]>([]);
  const [name, setName] = useState("");
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("");
  const [website, setWebsite] = useState("");
  const [description, setDescription] = useState("");
  const [logoUrl, setLogoUrl] = useState("");

  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!session) {
    return null;
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const address = {
        street: trimOrUndefined(street),
        city: trimOrUndefined(city),
        postalCode: trimOrUndefined(postalCode),
        country: trimOrUndefined(country),
      };
      const hasAddress = Object.values(address).some(
        (value) => value !== undefined
      );

      await companiesApi.createCompany(session.accessToken, {
        userIds: userIds.length > 0 ? userIds : undefined,
        name: name.trim(),
        registrationNumber: trimOrUndefined(registrationNumber),
        email: trimOrUndefined(email),
        phoneNumber: trimOrUndefined(phoneNumber),
        address: hasAddress ? address : undefined,
        website: trimOrUndefined(website),
        description: trimOrUndefined(description),
        logoUrl: trimOrUndefined(logoUrl),
      });
      navigate("/companies", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setError(t("companies.create.validationError"));
      } else if (err instanceof ApiError && err.status === 409) {
        setError(t("companies.create.conflictError"));
      } else {
        setError(t("companies.create.genericError"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="create-company-page">
      <form className="create-company-page__form" onSubmit={handleSubmit}>
        <h1>{t("companies.create.title")}</h1>

        <UserIdsSelect value={userIds} onChange={setUserIds} />

        <label htmlFor="name">{t("companies.create.nameLabel")}</label>
        <input
          id="name"
          type="text"
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
        />

        <label htmlFor="registrationNumber">
          {t("companies.create.registrationNumberLabel")}
        </label>
        <input
          id="registrationNumber"
          type="text"
          value={registrationNumber}
          onChange={(event) => setRegistrationNumber(event.target.value)}
        />

        <label htmlFor="email">{t("companies.create.emailLabel")}</label>
        <input
          id="email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />

        <label htmlFor="phoneNumber">
          {t("companies.create.phoneNumberLabel")}
        </label>
        <input
          id="phoneNumber"
          type="tel"
          value={phoneNumber}
          onChange={(event) => setPhoneNumber(event.target.value)}
        />

        <label htmlFor="street">{t("companies.create.streetLabel")}</label>
        <input
          id="street"
          type="text"
          value={street}
          onChange={(event) => setStreet(event.target.value)}
        />

        <label htmlFor="city">{t("companies.create.cityLabel")}</label>
        <input
          id="city"
          type="text"
          value={city}
          onChange={(event) => setCity(event.target.value)}
        />

        <label htmlFor="postalCode">
          {t("companies.create.postalCodeLabel")}
        </label>
        <input
          id="postalCode"
          type="text"
          value={postalCode}
          onChange={(event) => setPostalCode(event.target.value)}
        />

        <label htmlFor="country">{t("companies.create.countryLabel")}</label>
        <input
          id="country"
          type="text"
          value={country}
          onChange={(event) => setCountry(event.target.value)}
        />

        <label htmlFor="website">{t("companies.create.websiteLabel")}</label>
        <input
          id="website"
          type="text"
          value={website}
          onChange={(event) => setWebsite(event.target.value)}
        />

        <label htmlFor="logoUrl">{t("companies.create.logoUrlLabel")}</label>
        <input
          id="logoUrl"
          type="text"
          value={logoUrl}
          onChange={(event) => setLogoUrl(event.target.value)}
        />

        <label htmlFor="description">
          {t("companies.create.descriptionLabel")}
        </label>
        <textarea
          id="description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          maxLength={2000}
        />

        {error && <p className="create-company-page__error">{error}</p>}

        <div className="create-company-page__actions">
          <button type="submit" disabled={isSubmitting}>
            {isSubmitting
              ? t("companies.create.submitting")
              : t("companies.create.submit")}
          </button>
          <Link to="/companies">{t("companies.create.cancel")}</Link>
        </div>
      </form>
    </div>
  );
}

export default CreateCompanyPage;
